/**
 * AppPanel Component
 * 
 * The main container component for displaying a Parse Server application.
 * Provides an integrated view with multiple sections:
 * - Preview: Live preview of the hosted web app
 * - Database: Browse database classes and data
 * - Cloud Code: View cloud functions and triggers
 * - Deployments: Deployment history
 * - Logs: System logs
 * 
 * This component replaces the previous PreviewPane and provides a more
 * comprehensive view of the application's backend resources.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ViewMode, ParseClass, CloudFunction, Trigger, Deployment, LogEntry, PublicFile } from '../../types/AppPanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { generateSubdomainFromAppName } from '../../utils/customDomain';
import { useCloudTree } from '../../hooks/useCloudTree';
import { useParseClasses } from '../../hooks/useParseClasses';
import { useClassData } from '../../hooks/useClassData';
import AppPanelHeader from './AppPanelHeader';
import PreviewView from './views/PreviewView';
import DatabaseView from './views/DatabaseView';
import CloudCodeView from './views/CloudCodeView';
import DeploymentsView from './views/DeploymentsView';
import LogsView from './views/LogsView';
import RelatedDataModal from './views/DatabaseView/RelatedDataModal';
import GlassLoader from '../GlassLoader';
import PublishModal from '../PublishModal';
import toast from 'react-hot-toast';
import back4app2 from '../../back4app2';
import { NetworkError, NotFoundError } from '@back4app2/sdk';
import NoAppCard from './NoAppCard';
import CreateAppModal, { CreateAppPhase } from '../AIAgentComp/CreateAppModal';
import LinkAppModal, { LinkAppPhase } from '../AIAgentComp/LinkAppModal';

/**
 * Props for opening Paddle checkout
 */
type PaddleCheckoutRequest = {
  container: HTMLDivElement;
  appId: string;
  appName?: string;
  ownerEmail?: string;
};

interface AppPanelProps {
  /** Parse app's associated ID (for subdomain operations) */
  associatedId?: string;
  /** Back4App Dashboard ID (UUID) - used for internal APIs like /parse-app/{id} and /cli/{id} */
  dashboardId?: string;
  /** Agent ID */
  agentId?: string;
  /** Agent name */
  agentName?: string;
  /** App name to display */
  appName?: string;
  /** URL to the backend dashboard */
  backendHref?: string;
  /** URL for the preview iframe */
  previewUrl?: string;
  /** Public URL (after publishing) */
  publicUrl?: string;
  /** External loading state (e.g., agent is working) */
  externalLoading?: boolean;
  /** Signal to force refresh preview */
  refreshSignal?: number;
  /** Paddle checkout callbacks */
  onOpenPaddleCheckout?: (request: PaddleCheckoutRequest) => void;
  onClosePaddleCheckout?: () => void;
  isCheckoutOpen?: boolean;
  isPaddleLoading?: boolean;
  paddleError?: string;
  paymentCompleted?: boolean;
  
  // Parse Server connection (for direct API calls)
  /** Parse Server URL (e.g., https://parseapi.back4app.com/parse) */
  parseServerURL?: string;
  /** Parse Application ID (X-Parse-Application-Id header) */
  parseAppId?: string;
  /** Master Key for Parse Server access */
  parseMasterKey?: string;
  
  // Backend data (can be passed in or fetched internally)
  /** Database classes - if not provided, will be fetched via hooks */
  parseClasses?: ParseClass[];
  /** Cloud functions list - if not provided, will be fetched via hooks */
  cloudFunctions?: CloudFunction[];
  /** Triggers list - if not provided, will be fetched via hooks */
  triggers?: Trigger[];
  /** Deployment history */
  deployments?: Deployment[];
  /** System logs */
  logs?: LogEntry[];
  /** Public files in hosting - if not provided, will be fetched via hooks */
  publicFiles?: PublicFile[];
  
  /** Callback when agent finishes a turn (to refresh data) */
  onAgentTurnEnded?: () => void;
  
  /** Callback when public files change (for mobile sync) */
  onPublicFilesChange?: (files: PublicFile[]) => void;
  /** Callback when current page changes (for mobile sync) */
  onCurrentPageChange?: (page: string) => void;
  /** External control of current page (for mobile) */
  externalCurrentPage?: string;
  /** Callback to trigger refresh from parent */
  onRefreshPreview?: () => void;
  /** Ref to expose publish handler for mobile */
  publishHandlerRef?: React.MutableRefObject<(() => void) | null>;
  /** Callback to report publish disabled state */
  onPublishStateChange?: (disabled: boolean, label: string) => void;
  /** Callback when view mode changes (for mobile sync) */
  onViewModeChange?: (mode: ViewMode) => void;
  /** External control of view mode (for mobile) */
  externalViewMode?: ViewMode;
  /** Callback when classes change (for mobile sync) */
  onClassesChange?: (classes: ParseClass[]) => void;
  /** Callback when selected class changes (for mobile sync) */
  onSelectedClassChange?: (className: string | null) => void;
  /** External control of selected class (for mobile) */
  externalSelectedClass?: string | null;
  /** Ref to expose database refresh handler for mobile */
  databaseRefreshHandlerRef?: React.MutableRefObject<(() => void) | null>;
  /** Callback to report database refreshing state */
  onDatabaseRefreshingChange?: (isRefreshing: boolean) => void;
  /** Callback when cloud code sub-view changes (for mobile sync) */
  onCloudCodeSubViewChange?: (subView: 'files' | 'functions') => void;
  /** External control of cloud code sub-view (for mobile) */
  externalCloudCodeSubView?: 'files' | 'functions';
  /** Ref to expose cloud code refresh handler for mobile */
  cloudCodeRefreshHandlerRef?: React.MutableRefObject<(() => void) | null>;
  /** Callback to report cloud code refreshing state */
  onCloudCodeRefreshingChange?: (isRefreshing: boolean) => void;

  // App modals (controlled by parent to survive AppPanel remounts)
  createAppModalOpen?: boolean;
  onCreateAppModalOpenChange?: (open: boolean) => void;
  linkAppModalOpen?: boolean;
  onLinkAppModalOpenChange?: (open: boolean) => void;
  // Controlled phase for CreateAppModal (to survive AppPanel remounts)
  createAppPhase?: CreateAppPhase;
  onCreateAppPhaseChange?: (phase: CreateAppPhase) => void;
  // Controlled phase for LinkAppModal (to survive AppPanel remounts)
  linkAppPhase?: LinkAppPhase;
  onLinkAppPhaseChange?: (phase: LinkAppPhase) => void;
}

export default function AppPanel({
  associatedId,
  dashboardId,
  agentId,
  agentName,
  appName: appNameProp,
  backendHref,
  previewUrl,
  publicUrl,
  externalLoading,
  refreshSignal,
  onOpenPaddleCheckout,
  onClosePaddleCheckout,
  isCheckoutOpen,
  isPaddleLoading,
  paddleError,
  paymentCompleted,
  parseServerURL,
  parseAppId,
  parseMasterKey,
  parseClasses: parseClassesProp,
  cloudFunctions: cloudFunctionsProp,
  triggers: triggersProp,
  deployments,
  logs,
  publicFiles: publicFilesProp,
  onPublicFilesChange,
  onCurrentPageChange,
  externalCurrentPage,
  publishHandlerRef,
  onPublishStateChange,
  onViewModeChange,
  externalViewMode,
  onClassesChange,
  onSelectedClassChange,
  externalSelectedClass,
  databaseRefreshHandlerRef,
  onDatabaseRefreshingChange,
  onCloudCodeSubViewChange,
  externalCloudCodeSubView,
  cloudCodeRefreshHandlerRef,
  onCloudCodeRefreshingChange,
  createAppModalOpen,
  onCreateAppModalOpenChange,
  linkAppModalOpen,
  onLinkAppModalOpenChange,
  createAppPhase,
  onCreateAppPhaseChange,
  linkAppPhase,
  onLinkAppPhaseChange,
}: AppPanelProps) {
  // Detect mobile for conditional sync behavior
  const isMobile = useIsMobile();
  const isInitialMountRef = useRef(true);
  
  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [cloudCodeSubView, setCloudCodeSubView] = useState<'files' | 'functions'>('files');
  
  // App data state
  const [appName, setAppName] = useState<string | undefined>(appNameProp);
  const [localPublicUrl, setLocalPublicUrl] = useState<string | undefined>(publicUrl);
  const [ownerEmail, setOwnerEmail] = useState<string | undefined>();
  const [isPaidPlan, setIsPaidPlan] = useState(false);
  
  // Publishing state
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // App linking UI (Preview-only empty state)
  // These can be controlled by the parent (MyAgents) so the modal stays open when AppPanel remounts.
  const isCreateModalControlled =
    typeof createAppModalOpen === 'boolean' &&
    typeof onCreateAppModalOpenChange === 'function';
  const isLinkModalControlled =
    typeof linkAppModalOpen === 'boolean' &&
    typeof onLinkAppModalOpenChange === 'function';

  const [internalShowCreateAppModal, setInternalShowCreateAppModal] = useState(false);
  const [internalShowLinkAppModal, setInternalShowLinkAppModal] = useState(false);

  const showCreateAppModal = isCreateModalControlled
    ? (createAppModalOpen as boolean)
    : internalShowCreateAppModal;

  const showLinkAppModal = isLinkModalControlled
    ? (linkAppModalOpen as boolean)
    : internalShowLinkAppModal;

  const openCreateAppModal = useCallback(() => {
    if (isCreateModalControlled) {
      onCreateAppModalOpenChange?.(true);
      return;
    }
    setInternalShowCreateAppModal(true);
  }, [isCreateModalControlled, onCreateAppModalOpenChange]);

  const closeCreateAppModal = useCallback(() => {
    if (isCreateModalControlled) {
      onCreateAppModalOpenChange?.(false);
      return;
    }
    setInternalShowCreateAppModal(false);
  }, [isCreateModalControlled, onCreateAppModalOpenChange]);

  const openLinkAppModal = useCallback(() => {
    if (isLinkModalControlled) {
      onLinkAppModalOpenChange?.(true);
      return;
    }
    setInternalShowLinkAppModal(true);
  }, [isLinkModalControlled, onLinkAppModalOpenChange]);

  const closeLinkAppModal = useCallback(() => {
    if (isLinkModalControlled) {
      onLinkAppModalOpenChange?.(false);
      return;
    }
    setInternalShowLinkAppModal(false);
  }, [isLinkModalControlled, onLinkAppModalOpenChange]);
  const dismissKey = useMemo(
    () => (agentId ? `b4a2_app_cta_dismissed_${agentId}` : undefined),
    [agentId]
  );
  const [isAppCtaDismissed, setIsAppCtaDismissed] = useState<boolean>(() => {
    if (!agentId) return false;
    try {
      return localStorage.getItem(`b4a2_app_cta_dismissed_${agentId}`) === '1';
    } catch {
      return false;
    }
  });

  // Keep dismissal state in sync if the selected agent changes
  useEffect(() => {
    if (!dismissKey) {
      setIsAppCtaDismissed(false);
      return;
    }
    try {
      setIsAppCtaDismissed(localStorage.getItem(dismissKey) === '1');
    } catch {
      setIsAppCtaDismissed(false);
    }
  }, [dismissKey]);
  
  // Database view state
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  
  // Database pagination state
  const [dbPage, setDbPage] = useState(0);
  const DB_PAGE_SIZE = 100;
  
  // Database sorting state (server-side)
  const [dbSortField, setDbSortField] = useState<string>('createdAt');
  const [dbSortDirection, setDbSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Preview navigation state
  const [currentPage, setCurrentPage] = useState<string | undefined>('/');
  const [localRefreshSignal, setLocalRefreshSignal] = useState(0);
  
  // Reset preview page when agent or app changes (safety net for timing issues)
  useEffect(() => {
    setCurrentPage('/');
  }, [agentId, associatedId]);
  
  // Section-specific refresh signals (for manual refresh buttons)
  const [deploymentsRefreshSignal, setDeploymentsRefreshSignal] = useState(0);
  
  // Related data modal state (for Pointer and Relation clicks)
  const [relatedDataModal, setRelatedDataModal] = useState<{
    isOpen: boolean;
    type: 'pointer' | 'relation';
    targetClass: string;
    objectId?: string;
    sourceClass?: string;
    sourceObjectId?: string;
    relationField?: string;
  } | null>(null);
  
  // Compute full preview URL with current page path
  // When CreateAppModal is open (create-from-agent flow), we intentionally "gate" the preview
  // so it doesn't mount/load until the user clicks Get started (which closes the modal).
  const isCreateAppModalOpen = showCreateAppModal;

  const previewUrlForUi = isCreateAppModalOpen ? undefined : previewUrl;
  const dashboardIdForPanelState = isCreateAppModalOpen ? undefined : dashboardId;

  const fullPreviewUrl = useMemo(() => {
    if (!previewUrlForUi) return undefined;
    
    // Normalize base URL
    let baseUrl = previewUrlForUi.trim();
    if (!baseUrl) return undefined;
    if (!/^https?:\/\//i.test(baseUrl)) {
      baseUrl = `https://${baseUrl}`;
    }
    // Remove trailing slash from base
    baseUrl = baseUrl.replace(/\/+$/, '');
    
    // Add current page path
    const path = currentPage || '/';
    if (path === '/') {
      return baseUrl;
    }
    
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${normalizedPath}`;
  }, [previewUrlForUi, currentPage]);

  // ============================================================================
  // Data Fetching Hooks
  // ============================================================================
  
  // Fetch cloud tree (public files + cloud code) from Back4App API
  const {
    tree: cloudTree,
    publicFiles: fetchedPublicFiles,
    cloudFiles: fetchedCloudFiles,
    cloudFunctions: fetchedCloudFunctions,
    triggers: fetchedTriggers,
    cloudCodeItems: fetchedCloudCodeItems,
    isLoading: isLoadingCloudTree,
    refetch: refetchCloudTree,
  } = useCloudTree(dashboardId);

  // Fetch database schemas from Parse Server
  // Note: parseAppId is the Parse Application ID (X-Parse-Application-Id)
  const {
    schemas: fetchedSchemas,
    classes: fetchedClasses,
    isLoading: isLoadingSchemas,
    refetch: refetchSchemas,
  } = useParseClasses({
    parseServerURL,
    appId: parseAppId,
    masterKey: parseMasterKey,
  });

  // Fetch data for selected class with pagination
  const {
    rows: classRows,
    count: classRowsTotal,
    isLoading: isLoadingClassData,
    refetch: refetchClassData,
  } = useClassData({
    parseServerURL,
    appId: parseAppId,
    masterKey: parseMasterKey,
    className: selectedClass || undefined,
    limit: DB_PAGE_SIZE,
    skip: dbPage * DB_PAGE_SIZE,
    orderBy: dbSortField,
    orderDirection: dbSortDirection,
  });

  // Reset page and sorting when class changes
  useEffect(() => {
    setDbPage(0);
    setDbSortField('createdAt');
    setDbSortDirection('desc');
  }, [selectedClass]);
  
  // Handle sort change from DataTable (server-side sorting)
  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setDbSortField(field);
    setDbSortDirection(direction);
    setDbPage(0); // Reset to first page when sorting changes
  }, []);

  // ============================================================================
  // Merged Data (props take precedence over fetched data)
  // ============================================================================
  
  const publicFiles = useMemo(() => 
    publicFilesProp || fetchedPublicFiles,
    [publicFilesProp, fetchedPublicFiles]
  );

  // Sync publicFiles with parent component
  useEffect(() => {
    if (publicFiles && onPublicFilesChange) {
      onPublicFilesChange(publicFiles);
    }
  }, [publicFiles, onPublicFilesChange]);

  // Sync currentPage with parent component
  useEffect(() => {
    if (currentPage && onCurrentPageChange) {
      onCurrentPageChange(currentPage);
    }
  }, [currentPage, onCurrentPageChange]);

  // Sync external currentPage from parent
  useEffect(() => {
    if (externalCurrentPage !== undefined && externalCurrentPage !== currentPage) {
      setCurrentPage(externalCurrentPage);
    }
  }, [externalCurrentPage]);

  // Cloud functions - now returned as CloudFunction[] directly from hook
  const cloudFunctions = useMemo(() => 
    cloudFunctionsProp || fetchedCloudFunctions,
    [cloudFunctionsProp, fetchedCloudFunctions]
  );

  // Triggers - now returned as Trigger[] directly from hook
  const triggers = useMemo(() => 
    triggersProp || fetchedTriggers,
    [triggersProp, fetchedTriggers]
  );

  // Use classes from props or fetched data (now includes row counts from useParseClasses)
  const parseClasses = useMemo(() => 
    parseClassesProp || fetchedClasses,
    [parseClassesProp, fetchedClasses]
  );

  // Computed values
  const hasApp = useMemo(() => !!dashboardId, [dashboardId]);
  const buttonText = useMemo(() => (localPublicUrl ? 'Domain Settings' : 'Publish'), [localPublicUrl]);
  const initialSuggestion = useMemo(() => generateSubdomainFromAppName(appName), [appName]);
  
  // Current host for subdomain validation
  const currentHost = useMemo(() => {
    if (!localPublicUrl) return undefined;
    try {
      return new URL(localPublicUrl).host;
    } catch {
      return undefined;
    }
  }, [localPublicUrl]);

  // Backend dashboard URL
  const backendOverviewHref = useMemo(() => {
    if (!backendHref) return undefined;
    return /^https?:\/\//i.test(backendHref) ? backendHref : `https://${backendHref}`;
  }, [backendHref]);

  // Publish settings URL
  const publishHref = useMemo(() => {
    if (!backendOverviewHref) return undefined;
    const base = backendOverviewHref
      .replace(/\/?overview\/?$/i, '')
      .replace(/\/$/, '');
    return `${base}/domain-settings`;
  }, [backendOverviewHref]);

  // Update app name when prop changes
  useEffect(() => {
    setAppName(appNameProp);
  }, [appNameProp]);

  // Update public URL when prop changes
  useEffect(() => {
    setLocalPublicUrl(publicUrl);
  }, [publicUrl]);

  // Check plan status when app is available
  // Note: getPlanData uses the Dashboard ID (UUID)
  useEffect(() => {
    if (!associatedId || !dashboardId || !parseAppId) return;
    
    (async () => {
      try {
        const planData = await back4app2.getPlanData(parseAppId || '');
        if (planData.canChangeSubdomain) {
          setIsPaidPlan(Boolean(planData.canChangeSubdomain));
        }
        const ownerData = await back4app2.getParseAppOwnerEmail(associatedId);
        setOwnerEmail(ownerData.ownerEmail);
      } catch (err) {
        let errMsg = '';
        if (err instanceof NetworkError) {
          console.error('network error', err);
          errMsg = 'Network error when fetching app settings. Check your internet connection and try again.';
        } else if (err instanceof NotFoundError) {
          errMsg = 'App not found!';
        } else {
          console.error('unexpected error fetching app settings', err);
          errMsg = 'Unexpected error while fetching app settings. Please try again.';
        }
        toast.error(errMsg, {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
          duration: 4000,
        });
      }
    })();
    
    // Default to free plan
    setIsPaidPlan(false);
  }, [associatedId, dashboardId, parseAppId]);

  // Update paid status after payment
  useEffect(() => {
    if (paymentCompleted) {
      setIsPaidPlan(true);
    }
  }, [paymentCompleted]);

  // Handle publish button click
  const handlePublishClick = useCallback(() => {
    if (!dashboardId) return;
    
    // If already published, open domain settings
    if (localPublicUrl && publishHref) {
      window.open(publishHref, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Otherwise, open publish modal
    setShowPublishModal(true);
  }, [dashboardId, localPublicUrl, publishHref]);

  // Expose publish handler to parent (for mobile publish button)
  useEffect(() => {
    if (publishHandlerRef) {
      publishHandlerRef.current = handlePublishClick;
    }
    return () => {
      if (publishHandlerRef) {
        publishHandlerRef.current = null;
      }
    };
  }, [publishHandlerRef, handlePublishClick]);

  // Report publish state to parent (for mobile publish button)
  useEffect(() => {
    if (onPublishStateChange) {
      const disabled = !dashboardId || publishing;
      onPublishStateChange(disabled, buttonText);
    }
  }, [dashboardId, publishing, buttonText, onPublishStateChange]);

  // Sync viewMode with parent component (for mobile view mode selector)
  // On mobile, skip the initial mount to avoid loop when agent is deleted
  useEffect(() => {
    if (onViewModeChange) {
      // On mobile, skip notification during initial mount to prevent loop
      if (isMobile && isInitialMountRef.current) {
        isInitialMountRef.current = false;
        return;
      }
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange, isMobile]);

  // Sync external viewMode from parent (for mobile)
  useEffect(() => {
    if (externalViewMode !== undefined && externalViewMode !== viewMode) {
      setViewMode(externalViewMode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalViewMode]);

  useEffect(() => {
    setViewMode('preview');
  }, [agentId]);

  // Auto-select a class when classes load: prefer one with data, fallback to _User, then first available
  useEffect(() => {
    if (selectedClass || !parseClasses?.length) return;

    const classWithData = parseClasses.find(c => c.rowCount > 0);
    const userClass = parseClasses.find(c => c.name === '_User');
    const target = classWithData || userClass || parseClasses[0];
    setSelectedClass(target.name);
  }, [parseClasses, selectedClass]);

  // Sync classes with parent component (for mobile class selector)
  useEffect(() => {
    if (onClassesChange && parseClasses) {
      onClassesChange(parseClasses);
    }
  }, [parseClasses, onClassesChange]);

  // Sync selectedClass with parent component (for mobile class selector)
  useEffect(() => {
    if (onSelectedClassChange) {
      onSelectedClassChange(selectedClass);
    }
  }, [selectedClass, onSelectedClassChange]);

  // Sync external selectedClass from parent (for mobile)
  useEffect(() => {
    if (externalSelectedClass !== undefined && externalSelectedClass !== selectedClass) {
      setSelectedClass(externalSelectedClass);
    }
  }, [externalSelectedClass]);

  // Validate subdomain availability
  const handleValidateSubdomain = useCallback(
    async (candidate: string): Promise<boolean> => {
      if (!associatedId || !candidate) return false;
      
      try {
        return await back4app2.preCheckSubdomain(associatedId, candidate, currentHost);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not validate the subdomain.';
        toast.error(message, {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
          duration: 4000,
        });
        return false;
      }
    },
    [associatedId, currentHost]
  );

  // Handle publish from modal
  const handlePublishFromModal = useCallback(
    async (subdomain: string): Promise<string> => {
      if (!associatedId) {
        throw new Error('Missing Parse App identifier.');
      }

      setPublishing(true);

      try {
        const url = await back4app2.publishAppSubdomain(associatedId, subdomain, currentHost);
        
        // Update agent metadata if available
        if (agentId && agentName) {
          try {
            await back4app2.updateAgent(agentId, agentName, url);
          } catch (error) {
            console.error('Failed to sync agent with new host', error);
            toast.error('Domain published, but agent metadata was not updated. Please refresh the page.');
          }
        }

        setLocalPublicUrl(url);
        toast.success('App published successfully!', {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
          duration: 3000,
        });

        return url;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to publish app. Please try again.';
        toast.error(message, {
          className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
          duration: 4000,
        });
        throw error;
      } finally {
        setPublishing(false);
      }
    },
    [associatedId, currentHost, agentId, agentName]
  );

  // Handle manage domains action
  const handleManageDomains = useCallback(() => {
    if (publishHref) {
      window.open(publishHref, '_blank', 'noopener,noreferrer');
    }
  }, [publishHref]);

  // Handle preview refresh
  const handleRefresh = useCallback(() => {
    setLocalRefreshSignal(prev => prev + 1);
  }, []);

  // Handle database refresh (manual button)
  const handleRefreshDatabase = useCallback(() => {
    refetchSchemas();
    if (selectedClass) {
      refetchClassData();
    }
    toast.success('Database refreshed', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
      duration: 1500,
    });
  }, [refetchSchemas, refetchClassData, selectedClass]);

  // Expose database refresh handler to parent (for mobile refresh button)
  useEffect(() => {
    if (databaseRefreshHandlerRef) {
      databaseRefreshHandlerRef.current = handleRefreshDatabase;
    }
    return () => {
      if (databaseRefreshHandlerRef) {
        databaseRefreshHandlerRef.current = null;
      }
    };
  }, [databaseRefreshHandlerRef, handleRefreshDatabase]);

  // Report database refreshing state to parent (for mobile refresh button)
  useEffect(() => {
    if (onDatabaseRefreshingChange) {
      onDatabaseRefreshingChange(isLoadingSchemas || isLoadingClassData);
    }
  }, [isLoadingSchemas, isLoadingClassData, onDatabaseRefreshingChange]);

  // Handle cloud code refresh (manual button)
  const handleRefreshCloudCode = useCallback(() => {
    refetchCloudTree();
    toast.success('Cloud code refreshed', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
      duration: 1500,
    });
  }, [refetchCloudTree]);

  // Sync cloudCodeSubView with parent component (for mobile toggle)
  useEffect(() => {
    if (onCloudCodeSubViewChange) {
      onCloudCodeSubViewChange(cloudCodeSubView);
    }
  }, [cloudCodeSubView, onCloudCodeSubViewChange]);

  // Sync external cloudCodeSubView from parent (for mobile)
  useEffect(() => {
    if (externalCloudCodeSubView !== undefined && externalCloudCodeSubView !== cloudCodeSubView) {
      setCloudCodeSubView(externalCloudCodeSubView);
    }
  }, [externalCloudCodeSubView]);

  // Expose cloud code refresh handler to parent (for mobile refresh button)
  useEffect(() => {
    if (cloudCodeRefreshHandlerRef) {
      cloudCodeRefreshHandlerRef.current = handleRefreshCloudCode;
    }
    return () => {
      if (cloudCodeRefreshHandlerRef) {
        cloudCodeRefreshHandlerRef.current = null;
      }
    };
  }, [cloudCodeRefreshHandlerRef, handleRefreshCloudCode]);

  // Report cloud code refreshing state to parent (for mobile refresh button)
  useEffect(() => {
    if (onCloudCodeRefreshingChange) {
      onCloudCodeRefreshingChange(isLoadingCloudTree);
    }
  }, [isLoadingCloudTree, onCloudCodeRefreshingChange]);

  // Handle deployments refresh (manual button)
  const handleRefreshDeployments = useCallback(() => {
    setDeploymentsRefreshSignal(prev => prev + 1);
    toast.success('Deployments refreshed', {
      className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
      duration: 1500,
    });
  }, []);

  // Handle pointer click in database view - opens related data modal
  const handlePointerClick = useCallback((pointer: { className: string; objectId: string }) => {
    setRelatedDataModal({
      isOpen: true,
      type: 'pointer',
      targetClass: pointer.className,
      objectId: pointer.objectId,
    });
  }, []);

  // Handle relation click in database view - opens related data modal
  const handleRelationClick = useCallback((targetClass: string, sourceObjectId: string, field: string) => {
    setRelatedDataModal({
      isOpen: true,
      type: 'relation',
      targetClass,
      sourceClass: selectedClass || undefined,
      sourceObjectId,
      relationField: field,
    });
  }, [selectedClass]);

  // Close related data modal
  const closeRelatedDataModal = useCallback(() => {
    setRelatedDataModal(null);
  }, []);

  // Handle refresh all data (called after agent finishes)
  const handleRefreshAllData = useCallback(() => {
    refetchCloudTree();
    refetchSchemas();
    if (selectedClass) {
      refetchClassData();
    }
  }, [refetchCloudTree, refetchSchemas, refetchClassData, selectedClass]);

  // Refresh all data when refreshSignal changes (agent finished working)
  useEffect(() => {
    if (refreshSignal && refreshSignal > 0) {
      handleRefreshAllData();
    }
  }, [refreshSignal, handleRefreshAllData]);

  // Render the appropriate view based on viewMode
  const renderView = () => {
    switch (viewMode) {
      case 'preview':
        return (
          <PreviewView
            previewUrl={fullPreviewUrl}
            externalLoading={externalLoading}
            refreshSignal={(refreshSignal || 0) + localRefreshSignal}
          />
        );
      
      case 'database':
        return (
          <DatabaseView
            classes={parseClasses}
            selectedClass={selectedClass || undefined}
            rows={classRows}
            totalCount={classRowsTotal}
            isLoading={isLoadingClassData || isLoadingSchemas}
            dashboardUrl={backendOverviewHref}
            schemas={fetchedSchemas}
            // Pagination props
            currentPage={dbPage}
            pageSize={DB_PAGE_SIZE}
            onPageChange={setDbPage}
            // Sorting props (server-side)
            sortField={dbSortField}
            sortDirection={dbSortDirection}
            onSortChange={handleSortChange}
            // Related data handlers
            onPointerClick={handlePointerClick}
            onRelationClick={handleRelationClick}
          />
        );
      
      case 'cloud-code':
        return (
          <CloudCodeView
            tree={cloudTree}
            cloudFiles={fetchedCloudFiles}
            functions={cloudFunctions}
            triggers={triggers}
            cloudCodeItems={fetchedCloudCodeItems}
            isLoading={isLoadingCloudTree}
            dashboardUrl={backendOverviewHref}
            parseServerURL={parseServerURL}
            appId={parseAppId}
            masterKey={parseMasterKey}
            subView={cloudCodeSubView}
          />
        );
      
      case 'deployments':
        return (
          <DeploymentsView
            appId={dashboardId}
            dashboardUrl={backendOverviewHref}
            refreshSignal={(refreshSignal || 0) + deploymentsRefreshSignal}
          />
        );
      
      case 'logs':
        return (
          <LogsView
            logs={logs}
            dashboardUrl={backendOverviewHref}
            isLive
          />
        );
      
      default:
        return null;
    }
  };

  // Determine panel state for empty states
  const panelState = useMemo(() => {
    if (previewUrlForUi || dashboardIdForPanelState) return 'ready';
    if (externalLoading) return 'working';
    return 'awaiting';
  }, [previewUrlForUi, dashboardIdForPanelState, externalLoading]);

  return (
    <>
      <div className="flex flex-col w-full h-full glass-surface-rounded-none lg:rounded-xl overflow-hidden">
        {/* Header - hidden on mobile */}
        <div className="hidden lg:block">
          <AppPanelHeader
          appName={appName}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          publicUrl={localPublicUrl}
          backendHref={backendOverviewHref}
          hasApp={hasApp}
          publishButtonLabel={buttonText}
          publishDisabled={!dashboardId || publishing}
          onPublishClick={handlePublishClick}
          // Preview context
          publicFiles={publicFiles}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onRefresh={handleRefresh}
          previewUrl={previewUrlForUi}
          // Database context
          parseClasses={parseClasses}
          selectedClass={selectedClass}
          onClassChange={setSelectedClass}
          // Cloud Code context
          cloudCodeSubView={cloudCodeSubView}
          onCloudCodeSubViewChange={setCloudCodeSubView}
          // Refresh handlers
          onRefreshDatabase={handleRefreshDatabase}
          onRefreshCloudCode={handleRefreshCloudCode}
          onRefreshDeployments={handleRefreshDeployments}
          isRefreshing={isLoadingCloudTree || isLoadingSchemas || isLoadingClassData}
        />
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden bg-black/40">
          {panelState === 'awaiting' && viewMode === 'preview' ? (
            // Awaiting instructions state (only for preview)
            <>
              {agentId && !isAppCtaDismissed ? (
                <NoAppCard
                  onCreate={openCreateAppModal}
                  onLink={openLinkAppModal}
                  onDismiss={() => {
                    if (!dismissKey) return;
                    try {
                      localStorage.setItem(dismissKey, '1');
                      setIsAppCtaDismissed(true);
                    } catch {
                      setIsAppCtaDismissed(true);
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-8 gap-8">
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/60">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <path d="M8 4V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span className="text-sm font-medium text-white">Awaiting further instructions</span>
                  </div>
                  <p className="text-lg font-medium text-center text-shimmer">
                    Once your Web App is created, your Preview will appear here
                  </p>
                </div>
              )}
            </>
          ) : panelState === 'working' && viewMode === 'preview' ? (
            // Working state (only for preview) - no backdrop-blur for Safari compatibility
            <>
              <div className="absolute inset-0 z-40 bg-black/80" />
              <GlassLoader label="Getting Ready" />
            </>
          ) : (
            // Normal content
            renderView()
          )}
        </div>

        {/* Publish Modal */}
        {showPublishModal && (
          <PublishModal
            initialSubdomain={initialSuggestion}
            open={showPublishModal}
            onClose={() => setShowPublishModal(false)}
            appId={parseAppId}
            appName={appName}
            isPaid={!!isPaidPlan}
            onValidateSubdomain={handleValidateSubdomain}
            onPublish={handlePublishFromModal}
            onManageDomains={handleManageDomains}
            onOpenPaddleCheckout={onOpenPaddleCheckout}
            onClosePaddleCheckout={onClosePaddleCheckout}
            isCheckoutOpen={!!isCheckoutOpen}
            isLoadingCheckout={!!isPaddleLoading}
            checkoutError={paddleError}
            ownerEmail={ownerEmail}
          />
        )}

        {/* App modals (triggered from the Preview empty-state CTA) */}
        <CreateAppModal
          open={showCreateAppModal}
          agentId={agentId}
          defaultAppName={appName ? `${appName} App` : 'My App'}
          onClose={closeCreateAppModal}
          onDone={closeCreateAppModal}
          phase={createAppPhase}
          onPhaseChange={onCreateAppPhaseChange}
        />
        <LinkAppModal
          open={showLinkAppModal}
          agentId={agentId}
          onClose={closeLinkAppModal}
          onDone={closeLinkAppModal}
          phase={linkAppPhase}
          onPhaseChange={onLinkAppPhaseChange}
        />

        {/* Related Data Modal (for Pointer/Relation clicks) */}
        {relatedDataModal && (
          <RelatedDataModal
            isOpen={relatedDataModal.isOpen}
            onClose={closeRelatedDataModal}
            type={relatedDataModal.type}
            targetClass={relatedDataModal.targetClass}
            objectId={relatedDataModal.objectId}
            sourceClass={relatedDataModal.sourceClass}
            sourceObjectId={relatedDataModal.sourceObjectId}
            relationField={relatedDataModal.relationField}
            parseServerURL={parseServerURL}
            parseAppId={parseAppId}
            masterKey={parseMasterKey}
            dashboardUrl={backendOverviewHref}
            onPointerClick={handlePointerClick}
          />
        )}
      </div>
    </>
  );
}

